import { z } from 'zod';

export const emailSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

export const otpSchema = z.object({
  otp: z
    .string()
    .length(6, 'OTP must be 6 digits')
    .regex(/^\d{6}$/, 'OTP must contain only digits'),
});

export const passwordLoginSchema = z.object({
  // Mismo mensaje que el resto de formularios: sin él zod devuelve «Invalid
  // email», que no está en validationMessages.ts y saldría en inglés siempre.
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});

/** Tramos de plantilla del alta. Espejo de COMPANY_SIZES del backend (register.dto.ts). */
export const COMPANY_SIZES = ['1-10', '11-50', '51-200', '201-1000', '1000+'] as const;

/**
 * Industrias que ofrece el alta (sept 2026). El `value` es el texto canonico
 * que se guarda en `co_companies.industry`; la etiqueta visible sale de i18n
 * (`register.industry.*`), asi que anadir una industria = valor aqui + clave
 * EN/ES en translations.ts.
 */
export const REGISTER_INDUSTRIES = [
  { value: 'Beverage (non-alcoholic)', key: 'register.industry.bevNonAlc' },
  { value: 'Beer, wine & spirits', key: 'register.industry.beerWineSpirits' },
  { value: 'Food & CPG', key: 'register.industry.foodCpg' },
  { value: 'Snacks & confectionery', key: 'register.industry.snacks' },
  { value: 'Beauty & personal care', key: 'register.industry.beauty' },
  { value: 'Health & wellness', key: 'register.industry.health' },
  { value: 'Tech & electronics', key: 'register.industry.tech' },
  { value: 'Automotive', key: 'register.industry.automotive' },
  { value: 'Retail & apparel', key: 'register.industry.retail' },
  { value: 'Hospitality & QSR', key: 'register.industry.hospitality' },
  { value: 'Cannabis & CBD', key: 'register.industry.cannabis' },
  { value: 'Other', key: 'register.industry.other' },
] as const;

/** Paso 1 del alta rediseñada: la cuenta (1.png — nombre completo, correo, contraseña, consentimiento). */
export const registerAccountStepSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Full name must be at least 2 characters')
    .max(120, 'Full name must be at most 120 characters'),
  email: z.string().email('Please enter a valid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password must be at most 128 characters'),
  consent: z.boolean().refine((v) => v === true, 'You must accept the terms to continue'),
});

/** Paso 2: la organización (2-4.png — tarjeta de tipo, nombre, industria, tamaño, región y la PRIMERA marca). */
export const registerOrgStepSchema = z.object({
  // CH-12: marca o agencia, sin tercera opción. El backend conserva «other»
  // (Property) para las cuentas que ya lo eligieron.
  entityKind: z.enum(['brand', 'agency']),
  orgName: z
    .string()
    .min(2, 'Organization name must be at least 2 characters')
    .max(120, 'Organization name must be at most 120 characters'),
  industry: z.string().min(1, 'Please select an industry'),
  companySize: z.string().min(1, 'Please select a company size'),
  primaryRegion: z.string().max(120, 'Keep the region under 120 characters'),
  // UNA sola marca a propósito: el +Add del mock se retiró — el resto se
  // añade luego desde el producto (NewBrandDialog).
  brandName: z
    .string()
    .min(2, 'Brand name must be at least 2 characters')
    .max(120, 'Brand name must be at most 120 characters'),
});

export const registerOtpStepSchema = otpSchema;

export type EmailInput = z.infer<typeof emailSchema>;
export type OtpInput = z.infer<typeof otpSchema>;
export type PasswordLoginInput = z.infer<typeof passwordLoginSchema>;
export type RegisterAccountInput = z.infer<typeof registerAccountStepSchema>;
export type RegisterOrgInput = z.infer<typeof registerOrgStepSchema>;
export type RegisterOtpInput = z.infer<typeof registerOtpStepSchema>;
